import React, { useEffect, useRef } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, Animated, ScrollView,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useGame } from '../../context/GameContext';
import { SCREEN_NAMES } from '../../utils/constant';
import { theme } from '../Common.style';

const OUTCOME_CONFIG = {
  VILLAGERS_WIN: {
    emoji: '🎉',
    title: 'Les Villageois ont gagné !',
    color: '#2e7d32',
    bg: '#e8f5e9',
  },
  IMPOSTOR_WIN_GUESSED: {
    emoji: '🕵️',
    title: "L'Imposteur a deviné !",
    color: '#c62828',
    bg: '#ffebee',
  },
  IMPOSTOR_WIN_WRONG_VOTE: {
    emoji: '😈',
    title: 'Mauvais vote — l\'Imposteur gagne !',
    color: '#c62828',
    bg: '#ffebee',
  },
};

const SCORE_DELTAS = {
  VILLAGERS_WIN: { villager: 2, impostor: 0 },
  IMPOSTOR_WIN_GUESSED: { villager: 0, impostor: 3 },
  IMPOSTOR_WIN_WRONG_VOTE: { villager: 0, impostor: 4 },
};

export default function GameResultScreen() {
  const navigation = useNavigation();
  const {
    room, currentRound, startLocalRound, endGame, localPlayers,
    currentUserId,
  } = useGame();

  const fadeAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(fadeAnim, { toValue: 1, duration: 600, useNativeDriver: true }).start();
  }, []);

  useEffect(() => {
    if (!room) {
      navigation.replace(SCREEN_NAMES.GAME_LOBBY);
    } else if (room.status === 'FINISHED') {
      navigation.navigate(SCREEN_NAMES.GAME_FINAL_SCORE);
    } else if (currentRound?.status === 'ROLE_REVEAL') {
      navigation.navigate(SCREEN_NAMES.GAME_ROLE_REVEAL);
    } else if (!currentRound) {
      navigation.replace(SCREEN_NAMES.GAME_LOBBY);
    }
  }, [room?.id, room?.status, currentRound?.id, currentRound?.status]);

  if (!room || !currentRound) return null;

  const isLocal = room.mode === 'LOCAL';
  const outcome = currentRound.outcome ?? 'VILLAGERS_WIN';
  const config = OUTCOME_CONFIG[outcome] ?? OUTCOME_CONFIG.VILLAGERS_WIN;
  const deltas = SCORE_DELTAS[outcome] ?? SCORE_DELTAS.VILLAGERS_WIN;

  const impostorName = isLocal
    ? (localPlayers[currentRound.impostorId]?.name ?? 'L\'Imposteur')
    : room.players?.find((p) => p.userId === currentRound.impostorId)?.user?.firstName ?? '?';

  const players = isLocal ? localPlayers : (room.players ?? []);

  const handlePlayAgain = () => {
    if (isLocal) {
      startLocalRound();
      navigation.navigate(SCREEN_NAMES.GAME_ROLE_REVEAL);
    } else {
      navigation.navigate(SCREEN_NAMES.GAME_WAITING_ROOM);
    }
  };

  const handleFinish = async () => {
    navigation.navigate(SCREEN_NAMES.GAME_FINAL_SCORE);
  };

  return (
    <ScrollView contentContainerStyle={[styles.container, { backgroundColor: config.bg }]}>
      <Animated.View style={{ opacity: fadeAnim, alignItems: 'center' }}>
        <Text style={styles.emoji}>{config.emoji}</Text>
        <Text style={[styles.title, { color: config.color }]}>{config.title}</Text>

        <View style={styles.revealBlock}>
          <Text style={styles.revealLabel}>L'Imposteur était</Text>
          <Text style={styles.impostorName}>{impostorName}</Text>
          <Text style={styles.revealLabel}>Mot secret</Text>
          <Text style={styles.secretWord}>{currentRound.secretWord}</Text>
        </View>

        <Text style={styles.scoresTitle}>Points ce round</Text>
        {isLocal
          ? localPlayers.map((p, i) => {
            const isImpostor = i === currentRound.impostorId;
            const delta = isImpostor ? deltas.impostor : deltas.villager;
            return (
              <View key={i} style={styles.scoreRow}>
                <Text style={styles.scoreName}>{p.name}</Text>
                {isImpostor && <Text style={styles.impostorTag}>Imposteur</Text>}
                <Text style={[styles.scoreDelta, delta > 0 && styles.scoreDeltaPos]}>
                  {delta > 0 ? `+${delta}` : '0'}
                </Text>
              </View>
            );
          })
          : players.map((p) => {
            const isImpostor = p.userId === currentRound.impostorId;
            const delta = isImpostor ? deltas.impostor : deltas.villager;
            return (
              <View key={p.id} style={styles.scoreRow}>
                <Text style={styles.scoreName}>{p.user?.firstName ?? 'Joueur'}</Text>
                {isImpostor && <Text style={styles.impostorTag}>Imposteur</Text>}
                <Text style={[styles.scoreDelta, delta > 0 && styles.scoreDeltaPos]}>
                  {delta > 0 ? `+${delta}` : '0'}
                </Text>
              </View>
            );
          })}

        <View style={styles.actions}>
          <TouchableOpacity style={styles.replayBtn} onPress={handlePlayAgain}>
            <Text style={styles.replayBtnText}>Rejouer</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.finishBtn} onPress={handleFinish}>
            <Text style={styles.finishBtnText}>Terminer la partie</Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1, alignItems: 'center', padding: 24, paddingBottom: 48,
  },
  emoji: { fontSize: 80, marginTop: 20, marginBottom: 8 },
  title: {
    fontSize: 26, fontWeight: '900', textAlign: 'center', marginBottom: 24,
  },
  revealBlock: {
    backgroundColor: '#fff', borderRadius: 16, padding: 20, width: '100%', alignItems: 'center', marginBottom: 24,
  },
  revealLabel: { color: '#888', fontSize: 13, marginTop: 8 },
  impostorName: {
    fontSize: 28, fontWeight: '900', color: '#333', marginBottom: 8,
  },
  secretWord: { fontSize: 36, fontWeight: '900', color: theme.colors.primary },
  scoresTitle: {
    fontWeight: '700', fontSize: 16, alignSelf: 'flex-start', marginBottom: 8, color: '#444',
  },
  scoreRow: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: 'rgba(0,0,0,0.06)', width: '100%',
  },
  scoreName: { flex: 1, fontSize: 16, color: '#333' },
  impostorTag: {
    fontSize: 11, color: '#c62828', fontWeight: '700', marginRight: 8, backgroundColor: '#ffebee', paddingHorizontal: 6, paddingVertical: 2, borderRadius: 8, overflow: 'hidden',
  },
  scoreDelta: {
    fontSize: 18, fontWeight: '800', color: '#bbb', minWidth: 32, textAlign: 'right',
  },
  scoreDeltaPos: { color: '#2e7d32' },
  actions: {
    flexDirection: 'row', gap: 12, marginTop: 32, width: '100%',
  },
  replayBtn: {
    flex: 1, backgroundColor: theme.colors.primary, borderRadius: 14, paddingVertical: 16, alignItems: 'center',
  },
  replayBtnText: { color: '#fff', fontWeight: '700', fontSize: 16 },
  finishBtn: {
    flex: 1, backgroundColor: '#fff', borderRadius: 14, paddingVertical: 16, alignItems: 'center', borderWidth: 1.5, borderColor: '#ddd',
  },
  finishBtnText: { color: '#555', fontWeight: '700', fontSize: 16 },
});
